import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { HeartPulseIcon, GraduationCapIcon, UsersIcon, SunIcon, MapPinIcon, BriefcaseIcon, ArrowRightIcon } from 'lucide-react';
const perks = [{
  icon: <HeartPulseIcon className="h-7 w-7" data-id="element-369" />,
  title: 'Health & Dental Benefits',
  description: 'Comprehensive medical, dental, and vision coverage for you and your family.'
}, {
  icon: <GraduationCapIcon className="h-7 w-7" data-id="element-370" />,
  title: 'Continuing Education',
  description: 'Tuition reimbursement and paid time off for professional development and certifications.'
}, {
  icon: <UsersIcon className="h-7 w-7" data-id="element-371" />,
  title: 'Collaborative Culture',
  description: 'Work alongside a passionate, multi-disciplinary team that supports each other every day.'
}, {
  icon: <SunIcon className="h-7 w-7" data-id="element-372" />,
  title: 'Work-Life Balance',
  description: 'Generous PTO, flexible scheduling, and a supportive environment that values your well-being.'
}];
const openings = [{
  title: 'Family Medicine Physician',
  department: 'Primary Care',
  type: 'Full-Time',
  location: 'Landover, MD',
  description: 'Provide comprehensive primary care to patients of all ages. Board certification in Family Medicine or Internal Medicine required.'
}, {
  title: 'Pediatric Nurse Practitioner',
  department: 'Pediatrics',
  type: 'Full-Time',
  location: 'Landover, MD',
  description: 'Deliver well-child visits, immunizations, and acute care for our pediatric population. NYS NP license required.'
}, {
  title: 'Urgent Care Physician',
  department: 'Urgent Care',
  type: 'Full-Time',
  location: 'Oxon Hill, MD',
  description: 'Treat walk-in patients for injuries, infections, and acute illnesses. Emergency or family medicine background preferred.'
}, {
  title: 'Medical Assistant',
  department: 'Clinical Support',
  type: 'Full-Time',
  location: 'Both Locations',
  description: 'Support providers with vitals, patient intake, and clinical documentation. CMA certification preferred.'
}, {
  title: 'Front Desk Coordinator',
  department: 'Operations',
  type: 'Full-Time',
  location: 'Landover, MD',
  description: 'Manage patient scheduling, check-in, and insurance verification. Bilingual English/Spanish strongly preferred.'
}, {
  title: 'Billing Specialist',
  department: 'Revenue Cycle',
  type: 'Full-Time',
  location: 'Oxon Hill, MD',
  description: 'Handle medical billing, claims submission, and insurance follow-up. Experience with eClinicalWorks a plus.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut'
    }
  }
};
export function CareersPage() {
  return <>
      <PageHero eyebrow="Join Our Team" title="Build Your Career While Building a Healthier Community" subtitle="At Healthy Choice Family Clinic, you'll find more than a job — you'll find a purpose. Join a team making a real difference in the lives of Maryland families." bgImage="https://images.unsplash.com/photo-1551190822-a9ce113ac100?w=1920&q=80" data-id="element-373" />

      <section className="py-20 md:py-28 bg-white" aria-labelledby="perks-heading" data-id="element-374">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-375">
          <div className="text-center mb-14" data-id="element-376">
            <h2 id="perks-heading" className="text-3xl md:text-4xl font-bold text-navy-500 mb-4" data-id="element-377">
              Why Healthy Choice?
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-378">
              We invest in our people because they're the heart of everything we
              do.
            </p>
          </div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-379">
            {perks.map(perk => <motion.div key={perk.title} variants={itemVariants} className="bg-gray-50 border border-[#E5E9F0] rounded-[10px] p-8 text-center" data-id="element-380">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-[10px] bg-[#0A71B1] text-white mb-5" data-id="element-381">
                  {perk.icon}
                </div>
                <h3 className="text-base font-bold text-navy-500 mb-2" data-id="element-382">
                  {perk.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-383">
                  {perk.description}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-gray-50" aria-labelledby="openings-heading" data-id="element-384">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-385">
          <div className="text-center mb-14" data-id="element-386">
            <h2 id="openings-heading" className="text-3xl md:text-4xl font-bold text-navy-500 mb-4" data-id="element-387">
              Open Positions
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-388">
              Explore current opportunities at our Landover and Oxon Hill
              locations.
            </p>
          </div>
          <motion.div className="space-y-4" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-389">
            {openings.map(job => <motion.a key={job.title} href="#" variants={itemVariants} className="group block bg-white border border-[#E5E9F0] rounded-[10px] p-6 md:p-8 hover:shadow-md hover:border-[#0A71B1]/30 transition-all duration-300" data-id="element-390">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-3" data-id="element-391">
                  <div data-id="element-392">
                    <h3 className="text-lg font-bold text-navy-500 group-hover:text-[#0A71B1] transition-colors" data-id="element-393">
                      {job.title}
                    </h3>
                    <p className="text-sm font-semibold text-teal-500" data-id="element-394">
                      {job.department}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-400" data-id="element-395">
                    <span className="flex items-center gap-1" data-id="element-396">
                      <BriefcaseIcon className="h-3.5 w-3.5" data-id="element-397" />
                      {job.type}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-398">
                      <MapPinIcon className="h-3.5 w-3.5" data-id="element-399" />
                      {job.location}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed mb-4" data-id="element-400">
                  {job.description}
                </p>
                <span className="text-sm font-semibold text-navy-500 flex items-center gap-1 group-hover:text-[#0A71B1] transition-colors" data-id="element-401">
                  Apply Now
                  <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-402" />
                </span>
              </motion.a>)}
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#0A71B1]" data-id="element-403">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-404">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-405">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-406">
              Don't See Your Role?
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto" data-id="element-407">
              We're always looking for talented, compassionate people. Send us
              your resume and we'll keep you in mind.
            </p>
            <a href="mailto:careers@hcfclinics.com" className="inline-flex items-center justify-center bg-[#E8891D] hover:bg-[#d07a15] text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors min-h-[48px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A71B1]" data-id="element-408">
              Send Your Resume
            </a>
          </motion.div>
        </div>
      </section>
    </>;
}